import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import { useDateTimeFormat } from '../../hooks/useDateTimeFormat';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, CheckCircle, XCircle, Users } from 'lucide-react';
import DriverLocations from './DriverLocations';
import GPSStatusOverview from './GPSStatusOverview';


export default function DashboardOverview() {
  const { token } = useAuth();
  const { formatDateTime } = useDateTimeFormat();
  const [stats, setStats] = useState(null);
  const [recentRides, setRecentRides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, todayRidesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/rides/today`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      
      // Filter to show ONLY today's rides on the Dashboard
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      const filteredTodayRides = todayRidesRes.data.filter(ride => {
        if (!ride.date_time) return false;
        const rd = new Date(ride.date_time);
        const rideDateStr = `${rd.getFullYear()}-${String(rd.getMonth() + 1).padStart(2, '0')}-${String(rd.getDate()).padStart(2, '0')}`;
        return rideDateStr === todayStr;
      });
      
      // Sort by status priority and then by time
      const statusPriority = { in_progress: 1, active: 2, no_show: 3, delayed: 4 };
      filteredTodayRides.sort((a, b) => {
        const priorityA = statusPriority[a.status] || 5;
        const priorityB = statusPriority[b.status] || 5;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return new Date(a.date_time) - new Date(b.date_time);
      });
      
      setRecentRides(filteredTodayRides);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-sky-100 text-sky-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-emerald-100 text-emerald-800',
      no_show: 'bg-amber-100 text-amber-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      {/* Mobile-friendly header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-slate-600 mt-1 lg:mt-2 text-sm lg:text-base">Overview of your transfer operations</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8" data-testid="dashboard-stats">
          <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm p-4 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <div className="h-10 w-10 lg:h-12 lg:w-12 bg-blue-100 rounded-lg lg:rounded-sm flex items-center justify-center">
                <Play className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" />
              </div>
              <span className="text-xs lg:text-sm font-medium text-slate-500 hidden lg:inline">In Progress</span>
            </div>
            <p className="text-2xl lg:text-4xl font-bold text-slate-900">{stats.in_progress_rides}</p>
            <p className="text-xs lg:text-sm text-slate-500 mt-1 lg:mt-2">In Progress</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm p-4 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <div className="h-10 w-10 lg:h-12 lg:w-12 bg-emerald-100 rounded-lg lg:rounded-sm flex items-center justify-center">
                <CheckCircle className="h-5 w-5 lg:h-6 lg:w-6 text-emerald-600" />
              </div>
              <span className="text-xs lg:text-sm font-medium text-slate-500 hidden lg:inline">Completed</span>
            </div>
            <p className="text-2xl lg:text-4xl font-bold text-slate-900">{stats.completed_rides}</p>
            <p className="text-xs lg:text-sm text-slate-500 mt-1 lg:mt-2">Completed</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm p-4 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <div className="h-10 w-10 lg:h-12 lg:w-12 bg-amber-100 rounded-lg lg:rounded-sm flex items-center justify-center">
                <XCircle className="h-5 w-5 lg:h-6 lg:w-6 text-amber-600" />
              </div>
              <span className="text-xs lg:text-sm font-medium text-slate-500 hidden lg:inline">No Show</span>
            </div>
            <p className="text-2xl lg:text-4xl font-bold text-slate-900">{stats.no_show_rides}</p>
            <p className="text-xs lg:text-sm text-slate-500 mt-1 lg:mt-2">No Show</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm p-4 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <div className="h-10 w-10 lg:h-12 lg:w-12 bg-slate-100 rounded-lg lg:rounded-sm flex items-center justify-center">
                <Users className="h-5 w-5 lg:h-6 lg:w-6 text-slate-600" />
              </div>
              <span className="text-xs lg:text-sm font-medium text-slate-500 hidden lg:inline">Drivers</span>
            </div>
            <p className="text-2xl lg:text-4xl font-bold text-slate-900">{stats.available_drivers}</p>
            <p className="text-xs lg:text-sm text-slate-500 mt-1 lg:mt-2">Drivers</p>
          </div>
        </div>
      )}

      {/* Today's Rides Section */}
      <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm shadow-sm">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-slate-200">
          <h2 className="text-lg lg:text-xl font-semibold text-slate-900">Today's Rides</h2>
        </div>
        
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Customer
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Date & Time
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Route
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Driver
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {recentRides.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                    No rides scheduled for today
                  </td>
                </tr>
              ) : (
                recentRides.map((ride) => (
                  <tr key={ride.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{ride.customer_name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">{formatDateTime(ride.date_time)}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <div className="max-w-xs">
                        <a 
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.pickup_location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate block text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {ride.pickup_location}
                        </a>
                        <a 
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.dropoff_location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-red-600 hover:text-red-800 font-medium block"
                        >
                          → {ride.dropoff_location}
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {ride.assigned_driver_name || 'Unassigned'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`status-badge ${getStatusColor(ride.status)}`}>
                        {ride.status.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden p-4 space-y-3">
          {recentRides.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              No rides scheduled for today
            </div>
          ) : (
            recentRides.map((ride) => (
              <div key={ride.id} className="bg-slate-50 rounded-xl p-4 space-y-3">
                {/* Header: Customer & Status */}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{ride.customer_name}</p>
                    <p className="text-sm text-slate-500 font-mono">{formatDateTime(ride.date_time)}</p>
                  </div>
                  <span className={`status-badge ${getStatusColor(ride.status)}`}>
                    {ride.status.replace('_', ' ')}
                  </span>
                </div>
                
                {/* Route */}
                <div className="space-y-1">
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.pickup_location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600"
                  >
                    <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
                    <span className="truncate">{ride.pickup_location}</span>
                  </a>
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.dropoff_location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-red-600"
                  >
                    <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></span>
                    <span className="truncate">{ride.dropoff_location}</span>
                  </a>
                </div>
                
                {/* Driver */}
                <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                  <Users className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {ride.assigned_driver_name || 'Unassigned'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* GPS Status and Driver Locations Section */}
      <div className="mt-4 lg:mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* GPS Status Overview - compact summary */}
        <div className="lg:col-span-1">
          <GPSStatusOverview />
        </div>
        {/* Driver Locations - detailed list */}
        <div className="lg:col-span-2">
          <DriverLocations />
        </div>
      </div>
    </div>
  );
}